import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { UserServiceFormModule } from '@app/account/teacher-page/user-service-form/user-service-form.module';
import { LocaleSharedModule } from '@app/i18n/locale-shared.module';
import { LibraryPanesModule } from '@app/library/panes/library-panes.module';
import { AbstractPane } from '@app/library/panes/models/abstract-pane';
import { MobileModalModule } from '@core/components/mobile-modal/mobile-modal.module';
import { ModalModule } from '@core/components/modal/modal.module';
import { AccountService, AuthService, ConfigService, LanguageService, ProfileService } from '@core/services';
import { NotVerifiedWidgetModule } from '@core/widgets';
import { LibraryRouter } from '@models/library.router';
import { Store } from '@ngrx/store';

import { IconComponent } from '@shared/icon/icon.component';
import { CLibraryFilterData } from '@store/models';
import { getLibraryRouteData } from '@store/reducers/library.reducer';
import {
  EContentPaneFormat,
  IContentPane,
  Language,
  Subject as BaseSubjects,
  SubjectsRegistry,
  User,
  UserProfile,
  UserSegmentEnum,
  UserService,
  UserServiceCategory,
  UserStatusEnum,
} from 'lingo2-models';
import { uniqBy } from 'lodash';
import { OnUiButtonModule, OnUiPopoverModule, PopoverPositionEnum } from 'onclass-ui';
import { combineLatest } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-user-services-stack',
  standalone: true,
  imports: [
    LocaleSharedModule,
    CommonModule,

    LibraryPanesModule,
    NotVerifiedWidgetModule,
    ModalModule,
    UserServiceFormModule,
    OnUiPopoverModule,
    MobileModalModule,
    OnUiButtonModule,
    IconComponent,
  ],
  templateUrl: './user-services-stack.component.html',
  styleUrls: ['./user-services-stack.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserServicesStackComponent extends AbstractPane implements OnInit {
  public panesList: IContentPane[] = [];
  public newService: UserService;
  public subjects: BaseSubjects[];
  public isGuest: boolean;
  public isTeacher: boolean;
  public isPopoverNeeded = false;
  public position = PopoverPositionEnum.bottom_right;

  private _userServiceCategory: UserServiceCategory[];
  private _language: Language;
  private _libraryRouteData: CLibraryFilterData;
  private _me: User;
  private _profile: Partial<UserProfile>;
  private _subjectsRegistry: SubjectsRegistry;
  private libRouter = new LibraryRouter();

  protected authService = inject(AuthService);
  protected configService = inject(ConfigService);
  protected languageService = inject(LanguageService);
  protected store = inject(Store);
  protected route = inject(ActivatedRoute);
  protected profileService = inject(ProfileService);

  ngOnInit(): void {
    combineLatest([
      this.languageService.language$,
      this.store.select(getLibraryRouteData),
      this.contextService.me$,
      this.configService.subjectsV2$.pipe(filter((v) => !!v)),
    ])
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: ([language, libraryRouteData, me, subjectsRegistry]) => {
          this._language = language;
          this._libraryRouteData = libraryRouteData;
          this._me = me;
          this.isGuest = this._me && this._me.status === UserStatusEnum.guest;
          this.isTeacher = AccountService.isAsIfTeacher(this._me);
          this._subjectsRegistry = subjectsRegistry;
          this.loadProfile();
          this.loadUserServiceCategory();
          this.subjects = this.setSubjects();
          this.markForCheck();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  public get isAsIfTeacher(): boolean {
    return AccountService.hasSegment(this._me, UserSegmentEnum.as_if_teacher);
  }

  public goCreateUserService() {
    if (this.isGuest) {
      this.authService.showAuthModal(() => this.onCreate());
    } else {
      this.onCreate();
    }
  }

  public onCreate() {
    this.newService = new UserService({
      author_id: this._me?.id,
    });
  }

  public profile() {
    return this._profile;
  }

  public setSubjects(): BaseSubjects[] {
    return uniqBy(
      [
        ...this._subjectsRegistry.other,
        ...this._subjectsRegistry.user,
        ...this._subjectsRegistry.native,
        ...this._subjectsRegistry.foreign,
        ...this._subjectsRegistry.non_categorized,
        ...this._subjectsRegistry.recent,
      ],
      'id',
    );
  }

  public onCancel() {
    this.newService = null;
    this.markForCheck();
  }

  public onChange() {
    this.newService = null;
    this.markForCheck();
  }

  public trackByFn(index, pane: IContentPane): number {
    return pane.order;
  }

  private loadUserServiceCategory() {
    this.configService
      .getUserServiceCategories(this._language?.code)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (userServiceCategory) => {
          this._userServiceCategory = userServiceCategory;
          this.panesList = this.generatePanesList();
          this.markForCheck();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  private loadProfile() {
    this.profileService
      .getProfile(this._me?.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (profile) => {
          this._profile = profile;
          this.markForCheck();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  private generatePanesList(): IContentPane[] {
    const pane: IContentPane = {
      order: 0,
      format: EContentPaneFormat['user-services-drag-scroll'],
      loaded: false,
      hidden: false,
      title: 'meetings.titles.personal',
      user_services_view: 'wide',
      rows: 4,
    };

    this.libRouter.setBaseRoute(this._libraryRouteData?.baseRoute);
    this.libRouter.setSubjects(this._libraryRouteData?.subjectCodes);

    return this._userServiceCategory.map((_usc, index) => {
      this.libRouter.setContentTypes([_usc.code]);

      return {
        order: index,
        ...pane,
        title: _usc.title,
        routerUrlAll: this.libRouter?.getLink()?.path,
        userServiceFilter: {
          category_id: [_usc.id],
          language_id: [],
          subject_id: [],
        },
      };
    });
  }

  protected onContentLoaded(viaQuery?: boolean): void {
    this.isVisible = true;
  }

  hasContent(): boolean {
    return !!this._pane;
  }
}
